/**
 * FEditsPanel - Shows annotated document with AI changes (Zinsser pattern)
 *
 * Displays the foolscap_markdown_annotated content with <ins>/<del> tags
 * rendered as green highlights (additions) and red strikethrough (deletions).
 *
 * This is a READ-ONLY view - the Document tab shows the clean canvas.
 *
 * Uses the same pattern as ZinsserAIChangesPanel:
 * 1. Replace <ins>/<del> tags with styled spans (extracting markdown prefix)
 * 2. Parse the whole thing with marked.parse once
 */

import { useMemo, useCallback } from 'react'
import { marked } from 'marked'
import { useFStore } from '@/stores/useFStore'

// Configure marked to allow HTML passthrough
marked.use({
  breaks: true,
  gfm: true,
})

/**
 * Extract markdown prefix (list marker, header) from content.
 * Returns [prefix, rest] where prefix should stay outside the annotation span.
 */
function extractMarkdownPrefix(content: string): [string, string] {
  // Match leading whitespace + list markers (- * + or 1.) or headers (#)
  const match = content.match(/^([\s]*(?:[-*+]|\d+\.)[\s]+|[\s]*#{1,6}[\s]+)/)
  if (match) {
    return [match[1], content.slice(match[1].length)]
  }
  return ['', content]
}

interface FEditsPanelProps {
  onCardClick?: (cardId: string) => void
}

export function FEditsPanel({ onCardClick }: FEditsPanelProps) {
  const { foolscapMarkdownAnnotated } = useFStore()

  // Handle clicks on card links (card:UUID format)
  const handleClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    const link = target.closest('a')
    if (!link) return

    const href = link.getAttribute('href')
    if (!href) return

    // Check for card: protocol
    if (href.startsWith('card:')) {
      e.preventDefault()
      e.stopPropagation()
      const cardId = href.slice(5) // Remove 'card:' prefix
      if (cardId && onCardClick) {
        onCardClick(cardId)
      }
    }
  }, [onCardClick])

  // Convert annotated markdown to HTML, preserving <ins>/<del> tags
  // Uses the same proven pattern as ZinsserAIChangesPanel
  const annotatedHtml = useMemo(() => {
    if (!foolscapMarkdownAnnotated) return ''

    // Strategy: Move markdown prefixes OUTSIDE the annotation spans
    // So "- <ins>text</ins>" parses as a proper list item
    let content = foolscapMarkdownAnnotated

    // For insertions: move markdown prefix outside the span
    content = content.replace(/<ins>([\s\S]*?)<\/ins>/g, (_match, p1) => {
      const [prefix, rest] = extractMarkdownPrefix(p1)
      if (prefix && rest) {
        return `${prefix}<span class="annotation-addition">${rest}</span>`
      }
      return `<span class="annotation-addition">${p1}</span>`
    })

    // For deletions: move markdown prefix outside the span
    content = content.replace(/<del>([\s\S]*?)<\/del>/g, (_match, p1) => {
      const [prefix, rest] = extractMarkdownPrefix(p1)
      if (prefix && rest) {
        return `${prefix}<span class="annotation-deletion">${rest}</span>`
      }
      return `<span class="annotation-deletion">${p1}</span>`
    })

    // Parse markdown - marked preserves HTML tags by default
    const html = marked.parse(content, { async: false }) as string

    return html
  }, [foolscapMarkdownAnnotated])

  if (!foolscapMarkdownAnnotated) {
    return (
      <div className="h-full flex items-center justify-center px-6 text-center">
        <div className="text-muted-foreground text-sm max-w-sm space-y-2">
          <p className="font-medium text-foreground">No Edits Yet</p>
          <p>
            When I make changes to your Foolscap, they'll appear here with
            additions highlighted in green and deletions in red.
          </p>
          <p className="text-xs">
            Continue chatting to develop your story structure.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto px-4 pt-3 pb-20 md:pb-[30vh]">
      <div
        className="prose-z min-h-full"
        onClick={handleClick}
        dangerouslySetInnerHTML={{ __html: annotatedHtml }}
      />
      <style>{`
        .prose-z .annotation-addition {
          color: rgb(22, 163, 74) !important;
          background: none !important;
          border-bottom: none !important;
          padding: 0 !important;
        }
        .prose-z .annotation-deletion {
          color: rgb(220, 38, 38) !important;
          text-decoration: line-through;
          background: none !important;
          border-bottom: none !important;
          padding: 0 !important;
        }
        /* Match Canvas list spacing - marked creates loose lists with <p> inside <li> */
        .prose-z li {
          margin-bottom: 0.5rem;
        }
        .prose-z li:last-child {
          margin-bottom: 0;
        }
      `}</style>
    </div>
  )
}
